import Layout from "../components/Layout";
import { useRouter } from "@tanstack/react-router";
import {
  BookOpen,
  CheckCircle2,
  Dumbbell,
  MapPin,
  Shield,
  Star,
  Target,
  Users,
} from "lucide-react";
import { motion } from "motion/react";
import LeadForm from "../components/LeadForm";


const benefits = [
  {
    icon: Dumbbell,
    title: "Full-Body Conditioning",
    desc: "Every session builds strength, endurance, and athletic performance across your entire body.",
  },
  {
    icon: Shield,
    title: "Real-World Self-Defense",
    desc: "Learn to handle yourself in any situation — standing or on the ground.",
  },
  {
    icon: Target,
    title: "Confidence Under Pressure",
    desc: "Training pushes you past your comfort zone and builds composure when it matters most.",
  },
  {
    icon: BookOpen,
    title: "Multi-Discipline Skill Development",
    desc: "Master striking, wrestling, and Jiu Jitsu — and learn how to blend them in live situations.",
  },
];

const trainingFocuses = [
  {
    number: "01",
    title: "Striking",
    desc: "Develop effective punches, kicks, elbows, and combinations. Learn range management and defense from a Muay Thai foundation.",
  },
  {
    number: "02",
    title: "Wrestling & Takedowns",
    desc: "Control where the fight goes. Learn double-legs, clinch work, and takedown defense to dominate in any position.",
  },
  {
    number: "03",
    title: "Jiu Jitsu & Grappling",
    desc: "Finish fights and control opponents on the ground with submissions, sweeps, and dominant positions.",
  },
];

const fightTeamPoints = [
  "Structured training plans",
  "Competition prep and strategy",
  "Accountability and coaching",
  "Supportive, ego-free culture",
];

const audiences = [
  {
    icon: Star,
    title: "Beginners",
    desc: "No experience needed. We start from the ground up and build skills progressively.",
  },
  {
    icon: Dumbbell,
    title: "Fitness-Focused Students",
    desc: "Train hard, get in shape, and learn real skills at the same time.",
  },
  {
    icon: Users,
    title: "Aspiring Fighters",
    desc: "If competition is your goal, we have the coaching and structure to get you there.",
  },
];

const serviceAreas = ["Eugene", "Thurston", "Glenwood", "Fairmont"];

export default function MmaPage() {
  const router = useRouter();

  return (
    <Layout>
      <title>
        Mixed Martial Arts Classes in Springfield, OR | Superhero Jiu Jitsu
        Academy
      </title>
      <meta
        name="description"
        content="Learn MMA at Superhero Jiu Jitsu Academy in Springfield, OR. We combine Jiu-Jitsu, Muay Thai, and wrestling into a complete system for adults, teens, and kids of all skill levels."
      />

      {/* ── HERO SECTION ── */}
      <section
        className="relative min-h-[92vh] flex items-center"
        data-ocid="mma-hero"
      >
        {/* Background image */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('/assets/generated/mma-hero-training.dim_1200x700.jpg')",
          }}
          aria-hidden="true"
        />
        {/* Dark overlay */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(135deg, oklch(0.10 0.06 255 / 0.97) 0%, oklch(0.14 0.07 270 / 0.88) 50%, oklch(0.12 0.05 255 / 0.70) 100%)",
          }}
          aria-hidden="true"
        />
        {/* Gold accent overlay */}
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 50%, oklch(0.68 0.16 82) 0%, transparent 50%)",
          }}
          aria-hidden="true"
        />
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-accent" />

        <div className="relative container mx-auto px-4 sm:px-6 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left — copy */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
            >
              <span className="inline-block text-xs font-display font-semibold tracking-widest uppercase text-gold mb-4 border border-gold/40 px-3 py-1 rounded-full">
                MMA · Springfield, OR
              </span>
              <h1 className="font-display font-black text-4xl sm:text-5xl md:text-6xl text-hero-foreground leading-tight text-balance mb-5">
                MMA Training in{" "}
                <span className="text-gold">Springfield, OR</span>
              </h1>
              <p className="text-hero-foreground font-body text-lg leading-relaxed max-w-lg mb-8">
                Build a complete skill set with striking, wrestling, and Jiu
                Jitsu in one system.
              </p>
              <div className="flex flex-wrap gap-3">
                {[
                  "Striking & Kickboxing",
                  "Wrestling",
                  "Jiu Jitsu",
                  "Beginners Welcome",
                ].map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gold/20 border border-gold/40 text-sm font-body font-medium text-hero-foreground"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-gold flex-shrink-0" />
                    {tag}
                  </span>
                ))}
              </div>
            </motion.div>

            {/* Right — lead capture form */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
            >
              <div
                className="bg-primary/80 backdrop-blur-sm border border-gold/30 rounded-2xl p-6 sm:p-8 shadow-elevated relative overflow-hidden"
                data-ocid="mma-hero-form"
              >
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-accent" />
                <h2 className="font-display font-bold text-xl text-hero-foreground mb-1">
                  Book Your Free Class
                </h2>
                <p className="text-hero-foreground font-body text-sm mb-6 opacity-80">
                  First class is on us. No experience required. Must book in
                  advance.
                </p>

                <LeadForm />

              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Gold divider */}
      <div className="h-1 bg-gradient-accent" />

      {/* ── INTRO SECTION ── */}
      <section className="bg-background py-16 px-4" data-ocid="mma-intro">
        <div className="container mx-auto max-w-5xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <span className="inline-block text-xs font-display font-semibold tracking-widest uppercase text-gold mb-3">
                Grappling + Striking + Wrestling
              </span>
              <h2 className="font-display font-black text-3xl sm:text-4xl text-foreground leading-tight mb-6 text-balance">
                A Complete Martial Arts System
              </h2>
              <div className="space-y-4 text-muted-foreground font-body text-base leading-relaxed">
                <p>
                  MMA is built on the idea that a complete fighter needs to be
                  dangerous everywhere — on the feet and on the ground. At
                  Superhero Jiu Jitsu Academy, our MMA classes are designed to
                  build practical skills and steady confidence. We combine
                  Jiu-Jitsu, sharp striking, and wrestling into a complete
                  system for adults, teens, and kids.
                </p>
                <p>
                  Each session helps you develop control, timing, and grit so
                  you can perform under pressure and keep growing on and off the
                  mats. Whether you're brand new to martial arts or looking to
                  sharpen your skills, our MMA program gives you the foundation
                  to thrive.
                </p>
              </div>
            </motion.div>
            {/* Visual card */}
            <motion.div
              initial={{ opacity: 0, x: 24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.15 }}
              className="relative rounded-2xl overflow-hidden shadow-elevated"
            >
              <img
                src="/assets/images/Superhero Jiu-Jitsu Acadmey_MMA.jpg"
                alt="Instructor coaching a student during Jiu Jitsu class"
                className="w-full object-cover aspect-[4/3]"
                loading="lazy"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── BENEFITS SECTION ── */}
      <section
        className="bg-muted/40 py-16 px-4 border-t border-border"
        data-ocid="mma-benefits"
      >
        <div className="container mx-auto max-w-4xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <span className="inline-block text-xs font-display font-semibold tracking-widest uppercase text-gold mb-3">
              Real Results
            </span>
            <h2 className="font-display font-black text-3xl sm:text-4xl text-foreground mb-12 text-balance">
              Why Train MMA?
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {benefits.map(({ icon: Icon, title, desc }, i) => (
              <motion.div
                key={title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: i * 0.08 }}
                className="bg-card rounded-2xl border border-border p-6 flex flex-col items-center text-center gap-4 shadow-card hover:shadow-elevated transition-smooth hover:-translate-y-0.5"
              >
                <span className="w-12 h-12 rounded-xl bg-gradient-accent flex items-center justify-center shadow-gold flex-shrink-0">
                  <Icon className="w-5 h-5 text-foreground" />
                </span>
                <div>
                  <h3 className="font-display font-bold text-base text-foreground mb-2">
                    {title}
                  </h3>
                  <p className="font-body text-sm text-muted-foreground leading-snug">
                    {desc}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TRAINING STRUCTURE SECTION ── */}
      <section className="bg-hero py-16 px-4" data-ocid="mma-training">
        <div className="container mx-auto max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-10"
          >
            <span className="inline-block text-xs font-display font-semibold tracking-widest uppercase text-gold mb-3">
              The MMA Curriculum
            </span>
            <h2 className="font-display font-black text-3xl sm:text-4xl text-hero-foreground text-balance mb-4">
              How We Train
            </h2>
            <p className="text-hero-foreground font-body text-base leading-relaxed max-w-2xl mx-auto opacity-80">
              Our MMA program blends Jiu-Jitsu, wrestling, and striking into a
              single, cohesive system. Each class covers a specific set of
              skills — takedowns and clinch, guard passing and submissions,
              strikes in combination — before bringing them together in live
              rounds.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
            {trainingFocuses.map(({ number, title, desc }, i) => (
              <motion.div
                key={number}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: i * 0.1 }}
                className="bg-primary/60 backdrop-blur-sm border border-gold/20 rounded-2xl p-6 text-center"
              >
                <span className="font-display font-black text-4xl text-gold opacity-60 block mb-3">
                  {number}
                </span>
                <h3 className="font-display font-bold text-base text-hero-foreground mb-3">
                  {title}
                </h3>
                <p className="font-body text-sm text-hero-foreground leading-relaxed opacity-80">
                  {desc}
                </p>
              </motion.div>
            ))}
          </div>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="text-hero-foreground font-body text-base leading-relaxed text-center max-w-2xl mx-auto opacity-80"
          >
            If you are ready to push further, our fight team gives you a place
            to test your skills. Training is focused and accountable, and it
            helps you build composure and courage while preparing for
            competition. Whether you plan to compete or just want to train like
            a fighter, you will find a supportive, ego-free environment that
            helps you discover what you can do.
          </motion.p>
        </div>
      </section>

      {/* ── FIGHT TEAM SECTION ── */}
      <section
        className="bg-muted/40 py-16 px-4 border-t border-border"
        data-ocid="mma-fight-team"
      >
        <div className="container mx-auto max-w-5xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <span className="inline-block text-xs font-display font-semibold tracking-widest uppercase text-gold mb-3">
                Optional · For Serious Athletes
              </span>
              <h2 className="font-display font-black text-3xl sm:text-4xl text-foreground leading-tight mb-5 text-balance">
                Train Like a Competitor
              </h2>
              <p className="text-muted-foreground font-body text-base leading-relaxed mb-6">
                Our optional fight team is for students who want to take their
                training further. With structured training plans, accountability
                check-ins, and competition preparation, the fight team is where
                serious athletes are built. You will develop the composure,
                courage, and conditioning needed to step into competition — and
                to win.
              </p>
              <ul className="space-y-3">
                {fightTeamPoints.map((point) => (
                  <li
                    key={point}
                    className="flex items-center gap-3 font-body text-sm text-foreground"
                  >
                    <CheckCircle2 className="w-5 h-5 text-gold flex-shrink-0" />
                    {point}
                  </li>
                ))}
              </ul>
            </motion.div>
            {/* Visual card */}
            <motion.div
              initial={{ opacity: 0, x: 24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.15 }}
              className="relative rounded-2xl overflow-hidden shadow-elevated bg-hero min-h-[280px] flex items-center justify-center"
            >
              <div
                className="absolute inset-0 opacity-25"
                style={{
                  backgroundImage:
                    "radial-gradient(circle at 60% 40%, oklch(0.68 0.16 82) 0%, transparent 55%)",
                }}
                aria-hidden="true"
              />
              <div className="relative text-center p-10">
                <div className="w-20 h-20 rounded-full bg-gradient-accent flex items-center justify-center shadow-gold mx-auto mb-5">
                  <span className="text-4xl">🏆</span>
                </div>
                <p className="font-display font-bold text-xl text-hero-foreground mb-2">
                  Compete. Improve. Win.
                </p>
                <p className="font-body text-sm text-hero-foreground opacity-80">
                  Optional fight team for competition-focused athletes
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── WHO IT'S FOR SECTION ── */}
      <section className="bg-hero py-16 px-4" data-ocid="mma-who">
        <div className="container mx-auto max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-10"
          >
            <span className="inline-block text-xs font-display font-semibold tracking-widest uppercase text-gold mb-3">
              Everyone Welcome
            </span>
            <h2 className="font-display font-black text-3xl sm:text-4xl text-hero-foreground text-balance mb-4">
              WHO IT'S FOR
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {audiences.map(({ icon: Icon, title, desc }, i) => (
              <motion.div
                key={title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: i * 0.1 }}
                className="bg-primary/60 backdrop-blur-sm border border-gold/20 rounded-2xl p-6 flex flex-col gap-4"
              >
                <span className="w-11 h-11 rounded-xl bg-gradient-accent flex items-center justify-center shadow-gold flex-shrink-0">
                  <Icon className="w-5 h-5 text-foreground" />
                </span>
                <div>
                  <h3 className="font-display font-bold text-base text-hero-foreground mb-2">
                    {title}
                  </h3>
                  <p className="font-body text-sm text-hero-foreground leading-relaxed opacity-80">
                    {desc}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA SECTION ── */}
      <section
        className="py-20 px-4 text-center relative"
        style={{
          background:
            "linear-gradient(135deg, oklch(0.12 0.06 255) 0%, oklch(0.18 0.08 270) 50%, oklch(0.14 0.05 255) 100%)",
        }}
        data-ocid="mma-cta"
      >
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage:
              "radial-gradient(circle at 50% 50%, oklch(0.68 0.16 82) 0%, transparent 60%)",
          }}
          aria-hidden="true"
        />
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="relative container mx-auto max-w-2xl"
        >
          <span className="inline-block text-xs font-display font-semibold tracking-widest uppercase text-gold mb-3 border border-gold/40 px-3 py-1 rounded-full">
            First Class Is Free
          </span>
          <h2 className="font-display font-black text-3xl sm:text-4xl md:text-5xl text-hero-foreground leading-tight mb-4 text-balance">
            Start Your MMA Journey Today
          </h2>
          <p className="text-hero-foreground font-body text-lg leading-relaxed mb-3 max-w-lg mx-auto">
            Your first class is free. No commitment, no experience required —
            just show up and see what you can do.
          </p>
          <p className="text-hero-foreground font-body text-sm mb-8 opacity-70">
            Must book in advance.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              type="button"
              onClick={() => router.navigate({ to: "/contact-us" })}
              className="inline-flex items-center justify-center px-8 py-4 rounded-xl font-display font-bold text-base bg-gradient-accent text-foreground shadow-gold hover:opacity-90 active:scale-[0.98] transition-smooth cursor-pointer"
              data-ocid="mma-cta-button"
            >
              Book Your Free Class
            </button>
            <a
              href="tel:+15416003371"
              className="inline-flex items-center justify-center px-8 py-4 rounded-xl font-display font-bold text-base border-2 border-primary-foreground/60 text-hero-foreground hover:bg-primary-foreground/10 transition-smooth cursor-pointer"
              data-ocid="mma-cta-phone"
            >
              Call (541) 600-3371
            </a>
          </div>
        </motion.div>
      </section>

      {/* ── LOCAL SEO SECTION ── */}
      <section
        className="bg-background py-10 px-4 border-t border-border"
        data-ocid="mma-local-seo"
      >
        <div className="container mx-auto max-w-3xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex items-center justify-center gap-2 mb-4">
              <MapPin className="w-4 h-4 text-gold flex-shrink-0" />
              <span className="font-display font-semibold text-sm text-muted-foreground uppercase tracking-wider">
                Serving Springfield &amp; Surrounding Areas
              </span>
            </div>
            <h2 className="font-display font-bold text-lg text-foreground mb-3">
              MMA Classes in Springfield, OR
            </h2>
            <p className="font-body text-muted-foreground text-sm leading-relaxed max-w-2xl mx-auto mb-4">
              Offering MMA classes in Springfield, OR — proudly serving{" "}
              {serviceAreas.join(", ")}, and surrounding Lane County areas.
            </p>
            <address className="not-italic font-body text-sm text-muted-foreground mb-5">
              <span className="font-medium text-foreground">
                1485 Mohawk Blvd, Springfield, OR 97477
              </span>
              {" · "}
              <a
                href="tel:+15416003371"
                className="text-foreground font-medium hover:text-gold transition-colors-fast"
              >
                (541) 600-3371
              </a>
            </address>
            <div className="flex flex-wrap justify-center gap-2">
              {["Springfield", ...serviceAreas, "Lane County"].map((area) => (
                <span
                  key={area}
                  className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-muted border border-border text-sm font-body text-muted-foreground"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-gold flex-shrink-0" />
                  {area}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
